import React, { useState, useEffect, useCallback } from 'react';
import { Text } from '@cuemath/web-comps';
import { usePatchAgentDetails, invalidateAgents } from 'actions/support-agent';
import {
  GENERAL_TEAM_NAME,
  TEAM_PICKER_HELPER,
  AGENT_ACTIONS,
  CACHE_KEYS,
  ERROR_AUTO_CLEAR_MS,
  lastTeamDecisionBody,
} from '../../constants';
import { FormField, FormLabel, TeamPickerHelper, ConfirmText, BtnDanger } from './styled';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnPrimary,
  BtnSecondary,
  IconBtn,
  ModalErrorMsg,
} from '../../styled-comps';
import Icon from '../icon';
import TeamPicker from './team-picker';

const PENDING_KEEP = 'keep';
const PENDING_DELETE = 'delete';
const PENDING_UPDATE = 'update';

const EditTeamsModal = ({ agent, onClose }) => {
  const [teamIds, setTeamIds] = useState([]);
  const [decisionMode, setDecisionMode] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const { patch: patchAgent, isProcessing } = usePatchAgentDetails({
    onComplete: error => {
      if (error) {
        setSubmitError(typeof error === 'string' ? error : 'Action failed');
        setPendingAction(null);
        return;
      }
      invalidateAgents(CACHE_KEYS.AGENTS);
      setPendingAction(null);
      onClose();
    },
  });

  useEffect(() => {
    if (!agent) return;
    setTeamIds(
      (agent.teams || []).filter(t => t.name !== GENERAL_TEAM_NAME).map(t => t.id),
    );
    setDecisionMode(false);
    setSubmitError(null);
  }, [agent]);

  useEffect(() => {
    if (!submitError) return undefined;
    const t = setTimeout(() => setSubmitError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(t);
  }, [submitError]);

  const handleSave = useCallback(() => {
    if (!agent || isProcessing) return;
    if (teamIds.length === 0) {
      setDecisionMode(true);
      return;
    }
    setPendingAction(PENDING_UPDATE);
    patchAgent(agent.user_id, { action: AGENT_ACTIONS.UPDATE_TEAMS, team_ids: teamIds });
  }, [agent, isProcessing, teamIds, patchAgent]);

  const handleKeep = useCallback(() => {
    if (!agent || isProcessing) return;
    setPendingAction(PENDING_KEEP);
    patchAgent(agent.user_id, { action: AGENT_ACTIONS.UPDATE_TEAMS, team_ids: [] });
  }, [agent, isProcessing, patchAgent]);

  const handleDelete = useCallback(() => {
    if (!agent || isProcessing) return;
    setPendingAction(PENDING_DELETE);
    patchAgent(agent.user_id, { action: AGENT_ACTIONS.DELETE });
  }, [agent, isProcessing, patchAgent]);

  if (!agent) return null;

  const saveLabel =
    isProcessing && pendingAction === PENDING_UPDATE ? 'Saving...' : 'Save';
  const keepLabel =
    isProcessing && pendingAction === PENDING_KEEP ? 'Saving...' : 'Keep on general only';
  const deleteLabel =
    isProcessing && pendingAction === PENDING_DELETE
      ? 'Deleting...'
      : 'Delete agent entirely';

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <Text variant="body1Medium">
            {decisionMode ? 'Last specific team' : `Edit teams · ${agent.name}`}
          </Text>
          <IconBtn onClick={onClose}>
            <Icon name="close" size={16} />
          </IconBtn>
        </ModalHead>

        {decisionMode ? (
          <>
            <ModalBody>
              {submitError && <ModalErrorMsg>{submitError}</ModalErrorMsg>}
              <ConfirmText>{lastTeamDecisionBody(agent.name)}</ConfirmText>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setDecisionMode(false)}>Back</BtnSecondary>
              <BtnSecondary onClick={handleKeep} disabled={isProcessing}>
                {keepLabel}
              </BtnSecondary>
              <BtnDanger onClick={handleDelete} disabled={isProcessing}>
                {deleteLabel}
              </BtnDanger>
            </ModalFoot>
          </>
        ) : (
          <>
            <ModalBody $minHeight={360}>
              {submitError && <ModalErrorMsg>{submitError}</ModalErrorMsg>}
              <FormField>
                <FormLabel>Teams</FormLabel>
                <TeamPicker value={teamIds} onChange={setTeamIds} />
                <TeamPickerHelper>{TEAM_PICKER_HELPER}</TeamPickerHelper>
              </FormField>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={onClose}>Cancel</BtnSecondary>
              <BtnPrimary onClick={handleSave} disabled={isProcessing}>
                {saveLabel}
              </BtnPrimary>
            </ModalFoot>
          </>
        )}
      </ModalCard>
    </ModalOverlay>
  );
};

export default EditTeamsModal;
